import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import type { ArrayElementType } from '@/core/types/gengeric';
import { RouteNames } from '@/core/plugins/router/routeNames';
import {
  ChannelManagerCode,
  type GetReversePlacingVariantsQuery,
  type ReverseIntegrationAgeSettingsInput,
  type ReverseIntegrationQuery,
  ReverseMasterStep,
  type ReverseRestartIntegrationMutation,
  type ReverseStartIntegrationInput,
  type ReverseUpdatePlacingVariantsInput,
  useCheckIsNotStartedReverseInteractionQuery,
  useGetReversePlacingVariantsQuery,
  useReverseIntegrationQuery,
  useReverseRestartIntegrationMutation,
  useReverseStartIntegrationMutation,
  useUncompletedReverseInteractionQuery,
  useUpdateReversePlacingVariantsMutation,
  useUpdateReverseRoomCategoriesMutation,
  useUpdateReverseServiceGroupsMutation,
  useUpdateReverseTouristTypesMutation,
} from '@/graphql/generated';
import { useAccommodationObjectsStore } from '@/parts/objects/store/accommodationObjects';

export type PlacingVariantsTarification = ArrayElementType<
  NonNullable<GetReversePlacingVariantsQuery['reversePlacingVariants']>['placingVariantsTarification']
>;

const activeStep = ref(0);
const completedSteps = ref(0);
const channelManagerCode = ref<ChannelManagerCode>();

const integration = ref<ReverseIntegrationQuery['reverseIntegration']>();

const restartIntegrationRoomCategories =
  ref<
    NonNullable<NonNullable<ReverseRestartIntegrationMutation['reverseRestartIntegration']>['View']>['roomCategories']
  >();

const tariffScheduleCID = ref<string>();
const remoteInteractionUrl = ref<string | null>();

export const useReverseIntegrationStore = (isEdit = false) => {
  const $router = useRouter();
  const $route = useRoute();

  const interactionId = computed(() => $route.query.interactionId);
  const { activeAccount } = useAccommodationObjectsStore();

  const contractNumber = computed(() => activeAccount.value?.contractNumber);

  const setActiveStep = (step: number, onlyActive = false) => {
    activeStep.value = step;

    if (!onlyActive) {
      completedSteps.value = step - 1;
    }
  };

  const back = () => {
    if (activeStep.value === 0) {
      $router.push({
        name: RouteNames.CMList,
      });
    }

    setActiveStep(activeStep.value - 1, true);
  };

  const getUncompletedIntegration = async () => {
    if (!channelManagerCode.value) {
      return;
    }

    const { request } = useUncompletedReverseInteractionQuery({
      channelManagerCode: channelManagerCode.value,
      contractNumber: contractNumber.value,
    });

    const result = await request();

    return result;
  };

  const checkIsNotStartedIntegration = async () => {
    if (!interactionId.value) {
      return true;
    }

    const { request } = useCheckIsNotStartedReverseInteractionQuery({
      interactionId: interactionId.value,
      contractNumber: contractNumber.value,
    });

    const result = await request();

    if (result.data) {
      return result.data.checkIsNotStartedReverseInteraction?.isNotStarted;
    }

    return true;
  };

  const getIntegration = async () => {
    if (!interactionId.value) {
      return;
    }

    let data;

    restartIntegrationRoomCategories.value = undefined;

    if (isEdit) {
      const { request } = useReverseRestartIntegrationMutation({
        input: {
          interactionQuery: {
            interactionId: interactionId.value,
            contractNumber: contractNumber.value,
          },
        },
      });
 
      const result = await request();

      if (result.data) {
        data = result.data.reverseRestartIntegration.View?.interaction;
        restartIntegrationRoomCategories.value = result.data.reverseRestartIntegration.View?.roomCategories;
      }
    } else {
      const { request } = useReverseIntegrationQuery({
        interactionId: interactionId.value,
        contractNumber: contractNumber.value,
      });

      const result = await request();

      if (result.data) {
        data = result.data.reverseIntegration;
      }
    }

    if (data) {
      integration.value = data;

      checkActiveStep();
    }

    return data;
  };

  const checkActiveStep = () => {
    if (!integration.value) {
      return;
    }

    const masterStep = integration.value.master?.masterStep;

    if (masterStep === ReverseMasterStep.RoomCategoriesSelecting) {
      setActiveStep(2);
    }

    if (masterStep === ReverseMasterStep.ServiceGroupsSelecting) {
      setActiveStep(3);
    }

    if (masterStep === ReverseMasterStep.TouristTypesSelecting) {
      setActiveStep(4);
    }

    if (masterStep === ReverseMasterStep.PlacingVariantsSelecting) {
      setActiveStep(5);
    }
  };

  const startIntegration = async (payload: ReverseStartIntegrationInput['integrationSettings']) => {
    if (!channelManagerCode.value) {
      return;
    }

    const { request } = useReverseStartIntegrationMutation({
      input: {
        channelManagerCode: channelManagerCode.value,
        contractNumber: contractNumber.value,
        integrationSettings: payload,
      },
    });

    const response = await request();

    return response;
  };

  const clearIntegration = () => {
    integration.value = undefined;
    activeStep.value = 0;
    completedSteps.value = 0;
    setChannelManagerCode(undefined);
  };

  const updateRoomCategories = async (payload: string[]) => {
    const { request } = useUpdateReverseRoomCategoriesMutation({
      input: {
        interactionId: interactionId.value,
        contractNumber: contractNumber.value,
        roomCategoryCids: payload,
      },
    });

    const response = await request();

    if (response.data) {
      integration.value = response.data.reverseUpdateRoomCategories.View;
    }

    return response;
  };

  const updateServiceGroups = async (payload: string[]) => {
    const { request } = useUpdateReverseServiceGroupsMutation({
      input: {
        interactionQuery: {
          interactionId: interactionId.value,
          contractNumber: contractNumber.value,
        },
        serviceGroupCids: payload,
      },
    });

    const response = await request();

    if (response.data) {
      integration.value = response.data.reverseUpdateServiceGroups.View;
    }

    return response;
  };

  const updateTouristsTypes = async (payload: ReverseIntegrationAgeSettingsInput) => {
    const { request } = useUpdateReverseTouristTypesMutation({
      input: {
        interactionQuery: {
          interactionId: interactionId.value,
          contractNumber: contractNumber.value,
        },
        ageSettings: payload,
      },
    });

    const response = await request();

    if (response.data) {
      integration.value = response.data.reverseUpdateTouristTypes.View;
    }

    return response;
  };

  const getPlacingVariantsTarification = async () => {
    const { request } = useGetReversePlacingVariantsQuery({
      interactionQuery: {
        interactionId: interactionId.value,
        contractNumber: contractNumber.value,
      },
    });

    const response = await request();

    return response;
  };

  const updatePlacingVariants = async (payload: ReverseUpdatePlacingVariantsInput['roomPlacingVariants']) => {
    const { request } = useUpdateReversePlacingVariantsMutation({
      input: {
        interactionQuery: {
          interactionId: interactionId.value,
          contractNumber: contractNumber.value,
        },
        roomPlacingVariants: payload,
      },
    });

    const routeParams = $router.currentRoute.value.params;

    const response = await request();

    if (response.data) {
      tariffScheduleCID.value = response.data.reverseUpdatePlacingVariants.View?.tariffScheduleCID;
      remoteInteractionUrl.value = response.data.reverseUpdatePlacingVariants.View?.remoteInteractionUrl;
      window.history.replaceState({}, '', `/object/${routeParams.id}/${routeParams.acc_id}/tariffs/${tariffScheduleCID.value}`);
    }
    else {
      window.history.replaceState({}, '', `/object/${routeParams.id}/${routeParams.acc_id}/channel-manager/`);
    }

    return response;
  };

  const restartIntegration = async () => {
    const { request } = useReverseRestartIntegrationMutation({
      input: {
        interactionQuery: {
          interactionId: interactionId.value,
          contractNumber: contractNumber.value,
        },
      },
    });

    const response = await request();

    if (response.data) {
      integration.value = response.data.reverseRestartIntegration.View?.interaction;
      restartIntegrationRoomCategories.value = response.data.reverseRestartIntegration.View?.roomCategories;
    }

    return response;
  };

  const setChannelManagerCode = (code: ChannelManagerCode | undefined) => {
    channelManagerCode.value = code;
  };

  return {
    activeStep,
    integration,
    completedSteps,
    tariffScheduleCID,
    restartIntegrationRoomCategories,
    remoteInteractionUrl,
    channelManagerCode,
    setActiveStep,
    back,
    startIntegration,
    getUncompletedIntegration,
    checkIsNotStartedIntegration,
    getIntegration,
    clearIntegration,
    updateRoomCategories,
    updateServiceGroups,
    updateTouristsTypes,
    getPlacingVariantsTarification,
    updatePlacingVariants,
    restartIntegration,
    setChannelManagerCode,
  };
};
